import React, { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Login, Dashboard } from './views'
import { MantineProvider } from '@mantine/core'
import { SharedPropertyView } from './components/SharedPropertyView'
import 'rsuite/dist/styles/rsuite-default.css'
import 'antd/dist/antd.min.css'
import 'react-tagsinput/react-tagsinput.css'
import 'remixicon/fonts/remixicon.css'
import './css/rtl.css'
import './scss/app.scss'
import {
  setSettings,
  setArchived,
  setWilayas,
  setTypes,
  setSpecs,
  setTransactions,
  setCategories,
  fetchClientRdvNotifications,
  fetchPropRdvNotifications,
  fetchReminderNotifications,
  fetchSeeksMatchNotifications,
} from './store/actions'
import { useDispatch, useSelector } from 'react-redux'
import axios, { setHeaders } from './helpers/Api'
import AppLoader from './components/AppLoader'
import { Alert } from 'rsuite'
import { hasRight } from './helpers/rightsHelper' // AJOUT DU HELPER ICI

// Intercepteur pour masquer l'erreur inoffensive du ResizeObserver en développement
window.addEventListener('error', (e) => {
  if (e.message.includes('ResizeObserver loop completed with undelivered notifications') ||
    e.message.includes('ResizeObserver loop limit exceeded')) {
    e.stopImmediatePropagation();

    // Masque l'overlay rouge de Webpack s'il a déjà eu le temps d'apparaître
    const webpackOverlay = document.getElementById('webpack-dev-server-client-overlay');
    if (webpackOverlay) webpackOverlay.style.display = 'none';
  }
});

function App() {
  const dispatch = useDispatch()
  const auth = useSelector((state) => state.auth)
  const { i18n } = useTranslation()

  const fetchData = async () => {
    try {
      // 1. Chargement parallèle des données de configuration publiques (SANS les archives)
      const [
        settingsRes,
        transactionsRes,
        typesRes,
        specsRes,
        wilayasRes,
        categoriesRes,
      ] = await Promise.all([
        axios.get('/setting/fetch'),
        axios.get('/transaction/fetch'),
        axios.get('/property/type/fetch'),
        axios.get('/spec/fetch'),
        axios.get('/utils/wilayas'),
        axios.get('/category/fetch'),
      ])

      // 2. Mise à jour du store Redux
      dispatch(setSettings(settingsRes.data))
      dispatch(setTransactions(transactionsRes.data))
      dispatch(setTypes(typesRes.data))
      dispatch(setSpecs(specsRes.data))
      dispatch(setWilayas(wilayasRes.data))
      dispatch(setCategories(categoriesRes.data))

      // 3. Récupération des archives SEULEMENT SI on a le droit "archive" ou "properties READ"
      if (hasRight(auth?.userObj, 'properties', 'READ') || hasRight(auth?.userObj, 'archive')) {
        try {
          const archivedRes = await axios.get('/property/fetch', {
            params: { filters: { status: 'ARCHIVED' } },
          })
          dispatch(setArchived(archivedRes.data))
        } catch (e) {
          console.error('Erreur chargement archives', e)
        }
      }

      // 4. Lancement des notifications SEULEMENT SI on a les droits
      if (hasRight(auth?.userObj, 'client_appointments', 'READ')) {
        dispatch(fetchClientRdvNotifications())
      }
      if (hasRight(auth?.userObj, 'property_appointments', 'READ')) {
        dispatch(fetchPropRdvNotifications())
      }
      if (hasRight(auth?.userObj, 'archive')) {
        dispatch(fetchReminderNotifications()) // <-- Plus de 403 grâce au if !
      }
      if (hasRight(auth?.userObj, 'matching')) {
        dispatch(fetchSeeksMatchNotifications())
      }

    } catch (error) {
      console.error('Erreur chargement initial:', error)
      if (error.response && error.response.status !== 403) {
        Alert.error('Erreur Interne 500 - Chargement des données échoué')
      }
    }
  }

  let refresh = true
  useEffect(() => {
    if (auth?.accessToken && refresh) {
      const { accessToken, deviceKey, userObj } = auth
      setHeaders(accessToken, deviceKey, userObj.id)
      fetchData()
    }
  }, [auth])

  // Mettre à jour la direction RTL/LTR quand la langue change
  useEffect(() => {
    if (i18n?.language === 'ar') {
      document.documentElement.dir = 'rtl'
      document.documentElement.lang = 'ar'
    } else {
      document.documentElement.dir = 'ltr'
      document.documentElement.lang = i18n?.language || 'fr'
    }
  }, [i18n?.language])

  // --- NOUVEAU 2 : Détection de la route de partage public ---
  const isSharedRoute = window.location.pathname.startsWith('/p/')
  // -----------------------------------------------------------

  return (
    <MantineProvider
      theme={{ dir: i18n?.language === 'ar' ? 'rtl' : 'ltr' }}
      withGlobalStyles
      withNormalizeCSS
    >
      <div className="App">
        <React.Suspense fallback={<AppLoader />}>
          {isSharedRoute ? (
            <SharedPropertyView />
          ) : auth?.accessToken ? (
            <Dashboard />
          ) : (
            <Login />
          )}
        </React.Suspense>
      </div>
    </MantineProvider>
  )
}

export default App